import { skyFlightCapabilities } from './106-sky-capabilities.mjs';
import { skyFlightPointToLocal } from './92-sky-flight-frame.mjs';

export const SKY_BONUSES = Object.freeze({
  rapid: { label: 'RAPID FIRE', duration: 8 },
  power: { label: 'POWER SURGE', duration: 10 },
  shield: { label: 'AEGIS FIELD', duration: 6 },
  boost: { label: 'VECTOR SURGE', duration: 10 },
  focus: { label: 'TARGET FOCUS', duration: 10 },
});
const WAVE_SCHEDULE = ['rapid', 'shield', 'power'];

/** Combat availability follows authored objectives, never cursor placement,
 * trigger input or player-selected cover. Distances are metres in the shared
 * travel frame; scenery and protected/closed surfaces cannot spend reward time.
 * Call after encounter proxies and firing capabilities have been synchronized. */
export function skyRewardOpportunity(state) {
  if (
    !skyFlightCapabilities(state).weapons ||
    state.cathedral?.motionHeld ||
    state.journey?.phase === 'handoff'
  )
    return { combat: false, boss: false };
  const boss = Boolean(state.boss || state.leviathan || state.cathedral);
  const targets = [...(state.enemies ?? []), ...(state.boss ? [state.boss] : [])];
  const combat = targets.some((target) => {
    if (target.hp <= 0 || target.defeated || target.collisionEnabled === false) return false;
    if (target.vulnerable === false) return false;
    if (target.componentId) {
      const hp = target.objectiveHp ?? target.hp;
      return target.vulnerable === true && Number.isFinite(hp) && hp > 0;
    }
    if (!Number.isFinite(target.hp)) return false;
    if (target.kind === 'boss') return target.vulnerable === true;
    if (target.kind === 'pillar' || target.journeyReturn) return false;
    const point = state.flightFrame ? skyFlightPointToLocal(state.flightFrame, target) : target;
    // The authored playable lane can reach this travel corridor irrespective of
    // the pilot's current side, sight or an individual barrel's obstruction.
    return point && point.z >= -129 && point.z < 0 && Math.abs(point.x) <= 24;
  });
  return { combat, boss: boss && combat };
}

/** Latch the first living departure at the authoritative6m boundary. Invoke
 * immediately after physical movement and before projectile resolution, plus at
 * entry for already-crossed survivors. Animation resets cannot restore a mark. */
export function markSkyJourneyDeparture(state, enemy) {
  if (!enemy.journeyMember || enemy.hp <= 0 || enemy.z < 6) return;
  const squad = state.squads?.find((value) => value.id === enemy.squad);
  if (!squad || squad.firstPassEligible === false) return;
  squad.firstPassEligible = false;
  if (state.journey?.sector === state.sector) state.journey.masteryEligible = false;
}

/** Settle an actual required squad once after all original IDs die. The caller
 * owns score and kill currency; this ledger owns first-pass marks, the replacing
 * wave slot and one stage-only boss grant. Returns telemetry or null for an
 * unearned, duplicate or malformed settlement. No empty-wave mastery exists. */
export function grantSkyWaveReward(state, squad) {
  const journey = state.journey;
  if (
    state.status !== 'playing' ||
    !journey ||
    journey.sector !== state.sector ||
    !Array.isArray(journey.firstPassMarks) ||
    !state.squads?.includes(squad) ||
    !Number.isInteger(squad.total) ||
    squad.total < 1 ||
    squad.total > 8 ||
    squad.kills !== squad.total ||
    !Number.isInteger(squad.chapter) ||
    squad.chapter < 0 ||
    squad.rewardGrantSettled ||
    squad.journeyWave == null ||
    !Array.isArray(squad.members) ||
    squad.members.length !== squad.total ||
    new Set(squad.members).size !== squad.total ||
    squad.members.some((id) => !squad.defeatedMembers?.includes(id)) ||
    !Number.isInteger(squad.journeyWave) ||
    squad.journeyWave < 0 ||
    squad.journeyWave >= journey.waveCount ||
    journey.memberCounts?.[squad.journeyWave] !== squad.total
  )
    return null;
  squad.rewardGrantSettled = true;
  const firstPass = squad.firstPassEligible === true;
  journey.firstPassMarks[squad.journeyWave] = firstPass;
  if (!firstPass) journey.masteryEligible = false;
  let powerUp = null;
  if (firstPass) {
    const key = WAVE_SCHEDULE[squad.chapter % WAVE_SCHEDULE.length];
    const bonus = SKY_BONUSES[key];
    state.waveReward = {
      key,
      ...bonus,
      remaining: bonus.duration,
      active: skyRewardOpportunity(state).combat,
      waveId: squad.id,
      sector: state.sector,
    };
    powerUp = key;
  }
  const superchargeReady =
    journey.waveCount > 0 &&
    journey.launched === journey.waveCount &&
    journey.firstPassMarks.length === journey.waveCount &&
    journey.firstPassMarks.every((mark) => mark === true) &&
    !state.bossSupercharge;
  if (superchargeReady)
    state.bossSupercharge = {
      sector: state.sector,
      status: 'ready',
      remaining: 12,
      duration: 12,
    };
  return { firstPass, powerUp, superchargeReady };
}

/** Advance concurrent source timers in actionable combat seconds. Queued wave
 * rewards activate automatically; replacement never adds duration. Boss time
 * additionally requires an exposed boss objective and survives phase/rush joins.
 * Invalid deltas, menus and protected/targetless intervals cannot spend time. */
export function stepSkyRewardClocks(state, dt, opportunity = skyRewardOpportunity(state)) {
  if (state.status !== 'playing' || !Number.isFinite(dt) || dt <= 0) return;
  if (opportunity.combat) {
    for (const key of Object.keys(SKY_BONUSES)) {
      if (Number.isFinite(state.bonuses?.[key]) && state.bonuses[key] > 0)
        state.bonuses[key] = Math.max(0, state.bonuses[key] - dt);
    }
    const wave = state.waveReward;
    if (wave != null && wave.sector === state.sector && wave.remaining > 0) {
      wave.active = true;
      wave.remaining = Math.max(0, wave.remaining - dt);
    }
  }
  const charge = state.bossSupercharge;
  if (
    opportunity.boss &&
    charge != null &&
    charge.sector === state.sector &&
    charge.status !== 'spent'
  ) {
    charge.status = 'active';
    charge.remaining = Math.max(0, charge.remaining - dt);
    if (charge.remaining === 0) charge.status = 'spent';
  }
}

/** Effects have one normal strength regardless of their live source count.
 * A queued wave slot is visible telemetry, not an already active shield/damage. */
export function skyRewardActive(state, key) {
  return (
    (Number.isFinite(state?.bonuses?.[key]) && state.bonuses[key] > 0) ||
    (state?.waveReward != null &&
      state.waveReward.sector === state.sector &&
      state.waveReward.key === key &&
      state.waveReward.active &&
      state.waveReward.remaining > 0)
  );
}
